import React, { useEffect, useState } from "react";
import { Box, Typography, Grid, Button, useMediaQuery, Stack, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import CustomTextField from "../commoncomponents/CustomTextField";
import AddIcon from '@mui/icons-material/Add';
import ComponentHeader from "../commoncomponents/ComponentHeader";
import CustomAgGrid from "../commoncomponents/CustomAgGrid";
import CustomTextFieldWithSearch from "../commoncomponents/CustomTextFieldWithSearch";
import { useDispatch, useSelector } from "react-redux";
import { updateWorkOrderStatus } from "../../redux/actions/workOrderActionTypes";
import { v4 as uuidv4 } from 'uuid';
import CustomDialog from "../commoncomponents/CustomDialog";
import { useLocation } from "react-router-dom";
import { mandatoryFields } from "../../redux/actions/assetActions";
import RoutineInspectionQuantityNumberInput from "./RoutineInspectionQunatityNumberInput";
import RoutineInspectionBottomButtons from "./RoutineInspectionBottomButtons";
import riRepository from "../../api/repositories/riRepository";
import { processingPercentageValue, statusList } from "../../utility/message";


const AddRowDialogue = ({ open, onClose, editMode, rows, setRows }) => {

	const dispatch = useDispatch();
	const basicDetails = useSelector((state) => state?.routineInspecBasicdetails);
	const assetCategoryType = useSelector((state) => state?.serviceType);
	const serviceType = assetCategoryType?.assetType === "fem" ? "fem" : "bem";
	const categoryType = assetCategoryType?.category;

	const initialState = {
		itemDescription: "",
		itemPartCode: "",
		partCost: 0,
		quantity: 0,
	};

	const [itemData, setItemData] = useState(initialState || {});
	const [partCodeOptions, setPartCodeOptions] = useState({
		itemPartCode: [],
	});

	const handleOnchange = (name, value) => {

		const formatNumericValue = (value, previousValue) => {
			if (value === "") return ""; // Allow empty input
			if (/^\d*\.?\d*$/.test(value)) { // Allow only valid numeric input

				if (previousValue === "0.00") return value; // Replace `0.00` entirely with the user's input

				// If the input is purely zeros or starts with zeros and a decimal
				if (/^0+(\.0*)?$/.test(value)) {
					return "0"; // Keep it as "0" for clarity
				}

				// Remove leading zeros for integers
				if (!value.includes(".")) {
					return value.replace(/^0+/, ""); // Remove all leading zeros for integers
				}

				// If the input contains a decimal like "0.00454544"
				const [integerPart, decimalPart] = value.split(".");
				if (Number(integerPart) === 0) {
					return decimalPart ? decimalPart.replace(/^0+/, "") : ""; // Strip leading zeros in the decimal
				}

				// Default valid case
				return value.replace(/^0+/, ""); // Remove leading zeros
			}

			return previousValue; // Retain the previous valid value if input is invalid
		};



		const values = name === "partCost" ? formatNumericValue(value) : value;
		setItemData((previous) => {
			const updatedData = {
				...previous,
				[name]: values,
			};

			if (name === "itemPartCode") {
				updatedData.itemPartCode = value?.id ? { ...value, label: value?.itemCode } : "";
				updatedData.itemDescription = value?.description ?? "";
			} else if (name === "quantity") {
				updatedData.partCost = parseFloat((previous.itemPartCode?.unitCost || 0) * value).toFixed(2)
			};

			return updatedData;
		});
	};

	const fetchPartCode = async () => {
		const itemPartCode = await riRepository.fetchItemCodeoptions(serviceType);
		setPartCodeOptions((previous) => ({
			...previous,
			itemPartCode: itemPartCode?.map((item, index) => ({
				key: `option${index}`,
				label: `${item?.itemcode || ""} - ${item?.descriptionname || ""}`,
				unitCost: item?.unitcost || "",
				description: item?.descriptionname || "",
				itemCode: item?.itemcode || "",
				id: item?.id,
			})),
		}));
	};

	useEffect(() => {
		fetchPartCode();
	}, []);

	const handleCancel = () => {
		setItemData(initialState);
		onClose();
	};

	const handleSave = async (e) => {
		e.preventDefault();

		if (editMode?.dummy_id || editMode?.id) {
			if (editMode?.id) {
				// Update existing row
				const updatedRows = rows?.map((row) =>
					row.id === editMode?.id ? itemData : row
				);
				setRows(updatedRows);
			} else if (editMode?.dummy_id) {
				// Update existing row
				const updatedRows = rows?.map((row) =>
					row.dummy_id === editMode?.dummy_id ? itemData : row
				);
				setRows(updatedRows);
			};

			dispatch({ type: "UPDATE_PART_REPLACEMENT_ROUTINE_INSPECTION", payload: { ...itemData } });
		} else {
			// Add new row
			const newItem = {
				...itemData,
				[`${serviceType}unscheduledworkorderheaderId`]: basicDetails?.id,
				dummy_id: uuidv4(),
			};

			setRows((prevRows) => (prevRows ? [...prevRows, newItem] : [newItem]));
			dispatch({ type: "PART_REPLACEMENT_ROUTINE_INSPECTION", payload: { ...newItem } });
		}

		// Reset form and close modal
		onClose();
		setItemData(initialState);
	};

	useEffect(() => {
		if (editMode?.dummy_id || editMode?.id) {
			if (editMode?.dummy_id) {
				setItemData({
					dummy_id: editMode?.dummy_id || "",
					itemDescription: editMode?.itemDescription || "",
					itemPartCode: {
						key: "option",
						label: `${editMode.itemPartCode?.label || ""}`,
						unitCost: editMode?.itemPartCode?.unitCost || "",
						description: editMode?.itemPartCode?.description || "",
						id: editMode.itemPartCode?.id || "",
					},
					partCost: editMode.partCost || 0,
					quantity: editMode.quantity || 0,
				});
			} else if (editMode?.id) {
				setItemData({
					id: editMode?.id || "",
					itemDescription: editMode?.itemDescription || "",
					itemPartCode: {
						key: "option",
						label: `${editMode.itemPartCode?.label || ""}`,
						unitCost: editMode?.itemPartCode?.unitCost || "",
						description: editMode?.itemPartCode?.description || "",
						id: editMode.itemPartCode?.id || "",
					},
					partCost: editMode?.partCost || 0,
					quantity: editMode?.quantity || 0,
				});
			}

		} else {
			setItemData(initialState);
		}
	}, [editMode]);

	const isFormValid = () => {
		const { itemPartCode, itemDescription, quantity } = itemData;
		return (
			itemPartCode?.id && // Check if itemPartCode has a valid id
			itemDescription.trim() !== "" && // Ensure itemDescription is not empty
			quantity > 0
		);
	};

	return (
		<>
			<Dialog open={open} sx={{
				"& .MuiPaper-root": {
					height: "380px",
				},
			}} >
				<DialogTitle>
					<Typography sx={{ fontWeight: "bold", fontSize: "17px" }}> {editMode ? "Edit Part Replacement" : "Add Part Replacement"} </Typography>
				</DialogTitle>
				<DialogContent >
					<Grid container spacing={2}>
						<Grid item xs={6}>
							<CustomTextFieldWithSearch
								label="Item Code"
								id="itemPartCode"
								name="itemPartCode"
								value={itemData?.itemPartCode}
								onChange={(value) => handleOnchange("itemPartCode", value)}
								options={partCodeOptions?.itemPartCode || []}
								autoFocus
								isMandatoryField={true}
							/>
						</Grid>
						<Grid item xs={6}>
							<CustomTextField
								label="Item Description"
								id="itemDescription"
								name="itemDescription"
								onChange={(value) => handleOnchange("itemDescription", value)}
								value={itemData?.itemDescription}
							/>
						</Grid>
						<Grid item xs={6}>
							<RoutineInspectionQuantityNumberInput
								value={itemData.quantity}
								onChange={(newValue) => handleOnchange("quantity", newValue)}
								min={0}
								step={1}
								label="Quantity Used"
								required
								max={500}
							/>
						</Grid>
						<Grid item xs={6}>
							<CustomTextField
								label="Part Cost"
								value={itemData?.partCost || 0}
								name={"partCost"}
								onChange={(newValue) => handleOnchange("partCost", newValue)}
								id="partCost"
								textAlign="right"
								onlyNumbers={true}
								numbersLength={13}
								allowDecimal={true}
							/>
						</Grid>
					</Grid>
				</DialogContent>
				<DialogActions>
					<Button variant="contained" onClick={handleSave} disabled={!isFormValid()}>{editMode ? "UPDATE" : "ADD"}</Button>
					<Button variant="outlined" onClick={handleCancel}>CANCEL</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};


const RoutineInspectionPartReplacement = ({ setPage, page, buttonAttributes }) => {

	//prop value that holds all details about current page,buttons,header,etc., refer AssetMain.jsx for more info
	const findHeaderContent = buttonAttributes.find(
		(item) => item.currentPageName === page
	);


	function AddRowButton({ onClick }) {
		return (
			<Box sx={{ mt: 2 }}>
				<Button color="primary" variant="contained" startIcon={<AddIcon />} onClick={onClick}>
					Add Row
				</Button>
			</Box>
		);
	};

	const dispatch = useDispatch();
	const location = useLocation();
	const basicDetails = useSelector((state) => state.routineInspecBasicdetails);
	const assetCategoryType = useSelector((state) => state?.serviceType);
	const status = useSelector((state) => state?.workOrderStatus?.status);
	const processingPercentage = useSelector((state) => state?.workOrderStatus?.percentage);
	const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down('sm'));
	const partReplacementArray = basicDetails?.partReplacementRows || ""
	const serviceType = assetCategoryType?.assetType === "fem" ? "fem" : "bem";
	const viewmode = location.state?.viewmode;

	const [rows, setRows] = useState(partReplacementArray || []);
	const [gridAPI, setGridApi] = useState({});
	const [dialogOpen, setDialogOpen] = useState(false);
	const [deleteDialog, setDeleteDialog] = useState(false);
	const [deleteId, setDeleteId] = useState(null);
	const [editData, setEditData] = useState(null);

	const statusMapping = {
		"Open": 1,
        "Scheduled":2,
        "In Progress": 3,
        "Completed": 4,
        "Closed": 5,
        "Cancelled": 6,
        "Verified":8,
        "Not Done and Closed":7,
        "Completed-Outsource":9,
	};

	// Map the current status to the corresponding number
	const mappedStatus = statusMapping[status] || null; // If status doesn't exist, fallback to null or any default value
	const shouldEnableEdit = !viewmode && mappedStatus !== 6;
	const shouldEnableDelete = !viewmode && mappedStatus !== 6;


	useEffect(() => {
		setRows(partReplacementArray);
	}, [partReplacementArray]);

	const columns_Field = [
		{
			field: 'itemPartCode',
			headerName: 'Item / Parts Code',
			flex: 1,
			minWidth: isSmallScreen ? 150 : 200,
			valueGetter: (params) => params.data.itemPartCode ? params.data.itemPartCode.label : '',
		},
		{
			field: 'itemDescription',
			headerName: 'Item Description',
			flex: 1,
			minWidth: isSmallScreen ? 80 : 120,

		},
		{
			field: 'quantity',
			headerName: 'Quantity Used',
			flex: 1,
			minWidth: isSmallScreen ? 80 : 120,
			cellStyle: { textAlign: 'right' },

		},
		{
			field: 'partCost',
			headerName: 'Part Cost',
			flex: 1,
			minWidth: isSmallScreen ? 80 : 120,
			cellStyle: { textAlign: 'right' },

		}
	];

	const handleSubmit = async (e) => {
		e.preventDefault();

		const updatePayload = rows?.map((row) => ({
			itemcodeid: row?.itemPartCode?.id || "",
			quantityused: row?.quantity || "",
			partscost: row?.partCost || "",
			[`${serviceType}routineinspectionheaderId`]: row?.[`${serviceType}routineinspectionheaderId`] || "",
			[`${serviceType}routineinspectionheaderId`]: basicDetails?.id || "",
			id: row?.id || "",
			timestamp: row?.timeStamp || ""
		}));

		if (updatePayload?.length > 0) {
			const addItemPartCode = await riRepository.updateItemPartCode(serviceType, updatePayload, basicDetails?.id);
			dispatch({ type: "CLEAR_PART_REPLACEMENT_ROUTINE_INSPECTION", payload: [] });

			const itemCodeResponse = await riRepository.fetchItemCodeoptions(serviceType);
			const mapItemCode = (id) => {
				const ic = itemCodeResponse.find(ic => ic.id === id);
				return ic ? ic.itemcode : '';
			};

			const mapItemDesc = (id) => {
				const ic = itemCodeResponse.find(ic => ic.id === id);
				return ic ? ic.descriptionname : '';
			};

			const maptoUnitCost = (id) => {
				const ic = itemCodeResponse?.find(ic => ic?.id === id);
				return ic ? ic?.unitcost : '';
			};

			addItemPartCode?.forEach((row) => {
				const transformedObject = {
					itemDescription: mapItemDesc(row?.itemcodeid || ""),
					itemPartCode: {
						id: row?.itemcodeid || "",
						label: mapItemCode(row?.itemcodeid || ""),
						unitCost:maptoUnitCost(row?.itemcodeid) || "",
					},
					partCost: parseFloat(row?.partscost || 0).toFixed(2) || "",
					quantity: row?.quantityused || 0,
					id: row?.id || "",
					timeStamp: row?.timestamp || ""
				};

				dispatch({ type: "PART_REPLACEMENT_ROUTINE_INSPECTION", payload: transformedObject });
			});
		};

		if (processingPercentage >= 68 && !viewmode) {
			setPage("Completion Info");
			dispatch(mandatoryFields(false, "UPDATE_MANDATORY_REQUIRED_ENABLED"));
		} else if (viewmode == true || mappedStatus === 4 || mappedStatus === 5 || mappedStatus === 6 || mappedStatus === 7) {
			setPage("Completion Info");
			dispatch(mandatoryFields(false, "UPDATE_MANDATORY_REQUIRED_ENABLED"));
		} else {
			const statusId = basicDetails?.assessments?.repairType === "Outsource" ? statusList?.inProgressOutsource?.id : statusList?.inProgress?.id //To get the status ID
			await riRepository.workorderStatusValue(serviceType, basicDetails?.id, statusId, processingPercentageValue?.partReplacement, basicDetails?.category?.id);
			dispatch(updateWorkOrderStatus("STATUS_CHANGES", { status: status, percentage: processingPercentageValue?.partReplacement }));
			setPage("Completion Info");
			dispatch(mandatoryFields(false, "UPDATE_MANDATORY_REQUIRED_ENABLED"));
		};
	};

	const handleOpenDialog = () => {
		setEditData(null);
		setDialogOpen(true);
	};

	const handleCloseDialog = () => {
		setDialogOpen(false);
		setEditData(null);
	};

	const handleEdit = (row) => {
		setEditData(row);
		setDialogOpen(true);
	};

	const handleDelete = async (row) => {
		setDeleteDialog(true);
		setDeleteId(row);
	};

	const handleConfirm = () => {
		handleCloseDialog();
	};

	const handleCloseCancelDialog = () => {
		setDeleteDialog(false);
	};

	const handleConfirmDelete = async () => {
		try {
			if (deleteId?.id) {
				await riRepository.deleteItemPartCode(serviceType, deleteId?.id);
				const updatedRows = rows?.filter((row) => row?.id !== deleteId?.id);
				setRows(updatedRows);
				dispatch({ type: "DELETE_PART_REPLACEMENT_ROUTINE_INSPECTION", payload: { id: deleteId?.id } });
				setDeleteDialog(false);
			} else if (deleteId?.dummy_id) {
				const updatedRows = rows?.filter((row) => row?.dummy_id !== deleteId?.dummy_id);
				setRows(updatedRows);
				dispatch({ type: "DELETE_PART_REPLACEMENT_ROUTINE_INSPECTION", payload: { dummy_id: deleteId?.dummy_id } });
				setDeleteDialog(false);
			}

		} catch (error) {
			console.error(error);
		};
	};

	const getRows = async () => {
		const listRows = await riRepository.getPartReplacementRows(serviceType, basicDetails?.id);
		const mappedRows = listRows?.map(row => ({
			itemPartCode: {
				id: row?.itemcodeid || "",
				label: row?.itemcode || "",
			},
			itemDescription: row?.descriptionname || "",
			partCost: parseFloat(row?.partscost || 0).toFixed(2) || "",
			quantity: row?.quantityused || "",
			id: row?.partId || "",
			timeStamp: row?.timestamp || ""
		}));
		setRows(mappedRows);
	};

	// useEffect(() => {
	// 	getRows();
	// }, []);


	return (
		<>
			<ComponentHeader
				mainHeader={findHeaderContent?.mainHeader}
				subHeader={findHeaderContent?.subHeader}
				secondSubHeaderAndDate={findHeaderContent?.secondSubHeaderAndDate}
			/>
			<Box mt={{ xs: 1, md: 2 }} sx={{ marginTop: '0rem' }}>
				<form style={{ position: "relative" }} onSubmit={handleSubmit}>
					<Stack direction={'row'} alignItems={'center'} gap={1.5}>
						<Typography m={0} variant='title2' color={'text.primary'} fontWeight={600}>{basicDetails?.workOrderNo || ""}</Typography>
					</Stack>
					<Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
						{!viewmode && mappedStatus !== 6 && (<AddRowButton onClick={handleOpenDialog} />)}
					</Grid>
					<Box mb={3} />
					<CustomAgGrid
						refSet={gridAPI}
						rows={rows}
						columns={columns_Field}
						setGridApi={setGridApi}
						handleEdit={shouldEnableEdit ? (row) => handleEdit(row) : null}
						handleDelete={shouldEnableDelete ? (row) => handleDelete(row) : null}
					/>

					<Box>
						<RoutineInspectionBottomButtons page={page} buttonAttributes={buttonAttributes} />
					</Box>
				</form>
			</Box>

			<AddRowDialogue
				open={dialogOpen}
				onClose={handleCloseDialog}
				onConfirm={handleConfirm}
				editMode={editData}
				rows={rows}
				setRows={setRows}
			/>

			<CustomDialog
				open={deleteDialog}
				onClose={handleCloseCancelDialog}
				onConfirm={handleConfirmDelete}
				content="Are you sure you want to Delete?"
				cancelBtn="No"
				confirmBtn="Yes"
			/>
		</>
	);
};

export default RoutineInspectionPartReplacement;
